/* eslint-disable no-underscore-dangle */
import { useMemo } from 'react';
import { AnyAction, configureStore, PreloadedState, Store } from '@reduxjs/toolkit';
import getConfig from 'next/config';
import { NextRouter } from 'next/router';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import thunk from 'redux-thunk';
import type { PageProps } from 'types';
import { EnhancedReduxStore } from 'types/store';
import { rtkQueryErrorLogger } from './middlewares';

import { getFiltersFromHashedUrl } from 'components/zpFilter/filterUtils';
import ResumeBuilderService from 'services/resumeBuilderService';
import { api } from 'store/api';
import { userApi } from 'store/apis/user';
import { initialState as filtersInitialState } from 'store/searchFilter';
import { isUserOutsideUS } from 'utils';

// get all reducers
import reducer from './reducers';
import rootSaga from './sagas';

const { publicRuntimeConfig } = getConfig();

const isDev: boolean = !publicRuntimeConfig.isProd || process.env.NODE_ENV === 'development';

const sagaMiddleware = createSagaMiddleware();

const getMiddleware = () => {
  const middleware = [thunk, sagaMiddleware, api.middleware, userApi.middleware, rtkQueryErrorLogger];

  if (isDev) {
    middleware.push(
      createLogger({
        collapsed: true,
      })
    );
  }

  return middleware;
};

function initStore(preloadedState: PreloadedState<typeof reducer>) {
  return configureStore({
    reducer,
    middleware: getMiddleware(),
    preloadedState,
  });
}

const configuredStore = initStore({});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof configuredStore.getState>;

export type AppDispatch = typeof configuredStore.dispatch;

let store: EnhancedReduxStore<RootState>;

export const initializeStore = (preloadedState: Partial<RootState>, isServer: boolean) => {
  let _store = store ?? (initStore(preloadedState) as EnhancedReduxStore<RootState>);
  if (preloadedState && store) {
    _store = initStore({
      ...store.getState(),
      ...preloadedState,
    }) as EnhancedReduxStore<RootState>;
    store = undefined;
  }

  if (isServer) {
    return _store;
  }

  if (!store) {
    store = _store;
  }
  return _store;
};

export const useStore = (
  pageProps: PageProps<{
    resumeData: Record<string, unknown>;
    stripeSubscriptionData: Record<string, unknown>;
  }>,
  router: NextRouter
): Store<RootState, AnyAction> => {
  const initialState = useMemo<Partial<RootState>>(() => {
    const { data, user, locals } = pageProps;
    const criteria = data?.criteria;
    const companyID = data?.companyInfo?.companyID || criteria?.companyID;
    const parsedLocals = {
      ...(locals || {}),
      metadata: data?.metadata || {},
      criteria: {
        ...(locals?.criteria || {}),
        ...(criteria || {}),
        ...(companyID && { companyID }),
      },
    };
    return {
      user: {
        ...(user || {}),
        isLoggedIn: !!user?.isLoggedIn,
      },
      locals: {
        ...parsedLocals,
        criteria: {
          ...(parsedLocals.criteria || {}),
          userOutsideUS: isUserOutsideUS(parsedLocals.criteria),
        },
      },
      resumeBuilder: ResumeBuilderService.getInitialResumeBuilderState(
        data?.resumeData || {},
        data?.stripeSubscriptionData || {},
        router.query?.step as string,
        router.pathname === '/myResume',
        user || {}
      ),
      searchFilter: {
        ...filtersInitialState,
        initialFilters: getFiltersFromHashedUrl(window.location.href),
      },
    };
  }, [pageProps]);

  const isServer = typeof window === 'undefined';

  const store = useMemo(() => {
    return initializeStore(initialState, isServer);
  }, [initialState, isServer]);

  // Added hot reloading, Please Refer: https://redux.js.org/usage/configuring-your-store#hot-reloading
  if (isDev && module.hot) {
    module.hot.accept('./reducers', () => store.replaceReducer(reducer));
  }

  if (!isServer && !store.runSagaTask) {
    store.runSagaTask = () => {
      store.sagaTask = sagaMiddleware.run(rootSaga);
    };
    store.runSagaTask();
  }

  return store;
};
